import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog } from '@/components/ui';
import { usePublicMarketingPopup } from '@/lib/api';
import './MarketingPopup.css';

const ELIGIBLE_PATHS = new Set(['/', '/planos', '/faq']);
const ARM_DELAY_MS = 3000;
const SCROLL_RATIO = 0.5;
export const DISMISS_COOLDOWN_MS = 60_000;

function dismissalRemaining(revision: string): number {
  try {
    const dismissedAt = Number(sessionStorage.getItem(`shiba.marketing.dismissed.${revision}`));
    if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) return 0;
    return Math.max(0, DISMISS_COOLDOWN_MS - (Date.now() - dismissedAt));
  } catch {
    return 0;
  }
}

function rememberDismissal(revision: string): void {
  try {
    sessionStorage.setItem(`shiba.marketing.dismissed.${revision}`, String(Date.now()));
  } catch {
    // Sem storage o modal apenas respeita o estado atual da página.
  }
}

/** Somente páginas públicas de descoberta/compra; nunca auth, checkout ou console. */
export function MarketingPopupGate(): JSX.Element | null {
  const { pathname } = useLocation();
  const eligible = ELIGIBLE_PATHS.has(pathname) || pathname.startsWith('/perfis/');
  return eligible ? <MarketingPopup /> : null;
}

/** Exit intent + 50% do scroll, com cooldown curto entre apresentações. */
function MarketingPopup(): JSX.Element | null {
  const campaign = usePublicMarketingPopup();
  const [open, setOpen] = useState(false);
  const [wasDismissed, setWasDismissed] = useState(false);
  const data = campaign.data;
  const revision = data?.revision;

  useEffect(() => {
    if (!revision) return;
    setWasDismissed(dismissalRemaining(revision) > 0);
  }, [revision]);

  useEffect(() => {
    if (!revision || !wasDismissed) return;
    const remaining = dismissalRemaining(revision);
    if (remaining <= 0) {
      setWasDismissed(false);
      return;
    }
    const timer = window.setTimeout(() => setWasDismissed(false), remaining);
    return () => window.clearTimeout(timer);
  }, [revision, wasDismissed]);

  useEffect(() => {
    if (!data?.enabled || wasDismissed || open) return;
    let armed = false;
    const timer = window.setTimeout(() => { armed = true; }, ARM_DELAY_MS);
    const show = (): void => {
      if (armed) setOpen(true);
    };
    const onMouseOut = (event: MouseEvent): void => {
      if (event.clientY <= 0 && event.relatedTarget === null) show();
    };
    const onScroll = (): void => {
      const height = document.documentElement.scrollHeight;
      if (window.scrollY + window.innerHeight >= height * SCROLL_RATIO) show();
    };
    document.documentElement.addEventListener('mouseout', onMouseOut);
    document.addEventListener('mouseleave', show);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(timer);
      document.documentElement.removeEventListener('mouseout', onMouseOut);
      document.removeEventListener('mouseleave', show);
      window.removeEventListener('scroll', onScroll);
    };
  }, [data?.enabled, wasDismissed, open]);

  if (!data?.enabled || wasDismissed) return null;

  const close = (): void => {
    rememberDismissal(data.revision);
    setWasDismissed(true);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={close} size="lg" title={<span className="sr-only">{data.title}</span>}>
      <div className="marketing-popup">
        {data.imageUrl && (
          <div className="marketing-popup__media">
            <img src={data.imageUrl} alt={data.imageAlt} />
          </div>
        )}
        <div className="marketing-popup__content">
          <p className="marketing-popup__eyebrow">Oferta especial</p>
          <h3 className="marketing-popup__title">{data.title}</h3>
          <p className="marketing-popup__description">{data.description}</p>
          <p className="marketing-popup__price">{data.priceLabel}</p>
          <Link to="/planos" className="marketing-popup__cta" onClick={close}>
            Ver planos <ArrowRight size={18} aria-hidden="true" />
          </Link>
          <button type="button" className="marketing-popup__later" onClick={close}>Agora não</button>
        </div>
      </div>
    </Dialog>
  );
}
